import { getEndOfDay, getStartOfDay } from "@shared/feShared/utils/dates";
import { DateTime } from "luxon";

/**
 * Attempts to parse a string as a date and returns it as an ISO 8601 UTC string.
 * Primarily used for formatting date parameters in GraphQL queries.
 *
 * @param dateStr - String to parse as date (optional)
 * @returns ISO 8601 UTC string (e.g. "2023-04-01T00:00:00.000Z") or undefined if parsing fails
 *
 * @example
 * // Parse and convert to UTC ISO 8601
 * toISO("2023-04-01") // => "2023-04-01T00:00:00.000Z"
 *
 * // Handles undefined (e.g. from optional URL parameters)
 * toISO(undefined) // => undefined
 *
 * // Invalid inputs return undefined
 * toISO("not-a-date") // => undefined
 */
export const toISO = (date?: string | Date) => {
  if (!date) return undefined;

  const dt = typeof date === "string" ? DateTime.fromISO(date) : DateTime.fromJSDate(date);

  if (!dt.isValid) return undefined;
  return dt.toUTC().toISO();
};

/**
 * Converts a JavaScript Date object to an ISO 8601 date-only string (YYYY-MM-DD).
 * Used primarily for URL parameters and filters where time information isn't needed.
 *
 * @param date - JavaScript Date object to convert
 * @returns ISO 8601 date string or undefined if input is undefined
 *
 * @example
 * // Convert Date to ISO 8601 date string
 * toIsoDate(new Date("2024-01-15")) // => "2024-01-15"
 *
 * // Handle undefined input
 * toIsoDate(undefined) // => undefined
 */
export const toIsoDate = (date: Date | undefined) => {
  if (!date) return undefined;

  return DateTime.fromJSDate(date).toISODate() || undefined;
};

/**
 * Converts an ISO 8601 date string to a JavaScript Date object in the local timezone.
 * Used primarily for converting ISO 8601 dates from APIs/URLs to dates for UI components.
 *
 * @param date - ISO 8601 date string to convert
 * @returns JavaScript Date object in local timezone or undefined if input is undefined
 *
 * @example
 * // Convert ISO 8601 string to JS Date
 * toJsDate("2024-03-14") // => Date object
 *
 * // Handle undefined input
 * toJsDate(undefined) // => undefined
 */
export const toJsDate = (date: Date | string | undefined) => {
  if (!date) return undefined;

  if (date instanceof Date) {
    return DateTime.fromJSDate(date, { zone: "local" }).toJSDate();
  }

  return DateTime.fromISO(date, { zone: "local" }).toJSDate();
};

/**
 * Formats a Date object or ISO string into a human-readable date string in local timezone.
 * Used primarily for displaying dates in the UI with consistent formatting.
 *
 * @param date - Date object or ISO string to format
 * @param format - Luxon format string (defaults to "MM/dd/yyyy")
 * @returns Formatted date string in local timezone or empty string if input is invalid
 *
 * @example
 * // Format Date object
 * toDisplayDate(new Date("2024-03-14")) // => "03/14/2024"
 *
 * // Format ISO string
 * toDisplayDate("2024-03-14") // => "03/14/2024"
 *
 * // Handle undefined input
 * toDisplayDate(undefined) // => ""
 */
export const toDisplayDate = (date: Date | string | undefined, format = "MM/dd/yyyy") => {
  const jsDate = toJsDate(date);

  if (!jsDate) {
    return "";
  }

  return DateTime.fromJSDate(jsDate, { zone: "local" }).toFormat(format);
};

/**
 * Object containing functions to format date range boundaries for filtering.
 * Converts dates to UTC ISO 8601 strings aligned with the start or end of the day in local timezone.
 *
 * @example
 * // Format start of date range
 * formatDateRangeBoundary.start("2024-01-15") // => "2024-01-15T00:00:00.000Z"
 *
 * // Format end of date range
 * formatDateRangeBoundary.end("2024-01-15") // => "2024-01-15T23:59:59.999Z"
 *
 * // Handle undefined input
 * formatDateRangeBoundary.start(undefined) // => undefined
 */
export const formatDateRangeBoundary = {
  start: (date?: string | Date) => (date ? toISO(getStartOfDay(date)) : undefined),
  end: (date?: string | Date) => (date ? toISO(getEndOfDay(date)) : undefined),
};
